import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import ComingSoon from "@/components/ComingSoon";
import { api, CATEGORIES, CURTAIN_STANDARD_SIZE } from "@/lib/api";
import { SUB_COLLECTIONS, SUB_COLLECTION_BY_SLUG } from "@/lib/beddingConfig";
import { ArrowRight, ChevronLeft } from "lucide-react";

const HEADER_BANNER = {
  bedding: { image: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/5d96ae127f122e340202493ff75cd939ea9d1aaf56d7d13eb16d26279e7b7519.png", subtitle: "Premium · Export · Cotton Satin · Kids Bedding." },
  blankets: { image: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/441cb965fc168cde911cb24ee4aade2aa209f656e8042730963495897acc41fb.png", subtitle: "Wrap up in tactile warmth, season after season." },
  "cushion-covers": { image: "https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?auto=format&fit=crop&w=1600&q=80", subtitle: "Cushion covers, plush towels, soft pillows." },
  curtains: { image: "https://static.prod-images.emergentagent.com/jobs/1b235423-2870-43ca-b9e6-4a44f088b6f4/images/9789fdc267bb5924073b16d729ad3de095efc77b79ce1c2eff9b0c6d2308858c.png", subtitle: `Sheer & velvet — standard ${CURTAIN_STANDARD_SIZE}.` },
};

export default function Category() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const search = params.get("search") || "";
  const sub = params.get("sub") || "";

  const cat = CATEGORIES.find((c) => c.slug === slug);
  const subCollections = SUB_COLLECTIONS[slug] || [];
  const hasSubCollections = subCollections.length > 0;
  const activeSub = sub ? SUB_COLLECTION_BY_SLUG[sub] : null;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cat) return;
    // For categories with sub-collections, only fetch when sub is chosen (or search is provided)
    if (hasSubCollections && !sub && !search) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = new URLSearchParams({ category: slug });
    if (sub) q.set("sub_collection", sub);
    if (search) q.set("search", search);
    api.get(`/products?${q.toString()}`)
      .then((r) => setProducts(r.data || []))
      .finally(() => setLoading(false));
  }, [slug, search, sub, cat, hasSubCollections]);

  if (!cat) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h2 className="font-display text-3xl text-ink">Category not found</h2>
      </div>
    );
  }

  const banner = HEADER_BANNER[slug];

  return (
    <div data-testid={`category-page-${slug}`}>
      {/* Header banner */}
      <section className="relative h-[34vh] min-h-[240px] sm:h-[42vh] sm:min-h-[320px] overflow-hidden bg-cream-warm">
        <img src={banner.image} alt={cat.label} className="w-full h-full object-cover" loading="eager" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/45 to-ink/10 sm:from-ink/55 sm:via-ink/15 sm:to-transparent" />
        <div className="absolute inset-0 flex items-end pb-6 sm:pb-12">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 w-full text-cream">
            <div className="label-eyebrow !text-cream/80 !text-[10px] sm:!text-xs mb-2 sm:mb-3">Collection</div>
            <h1 className="font-display text-[1.9rem] leading-[1.1] sm:text-5xl lg:text-6xl tracking-tight !text-cream drop-shadow-[0_2px_12px_rgba(43,45,42,0.45)]">{cat.label}</h1>
            <p className="text-cream/90 max-w-xl mt-2 sm:mt-3 text-xs sm:text-base leading-relaxed">{banner.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Curtain size badge (only for curtains category) */}
      {slug === "curtains" && (
        <div className="bg-cream-warm border-b border-border-subtle">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-sm text-ink-soft">
            <span className="label-eyebrow !text-[10px] mr-3">Note</span>
            All curtains are crafted to a standard size of{" "}
            <span className="font-semibold text-ink">{CURTAIN_STANDARD_SIZE}</span>.
          </div>
        </div>
      )}

      {/* Sub-collection picker for bedding */}
      {hasSubCollections && !sub && !search && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="sub-collections">
          <div className="text-center mb-7 sm:mb-12">
            <div className="label-eyebrow !text-[10px] sm:!text-xs mb-1.5 sm:mb-2">Choose Your Collection</div>
            <h2 className="font-display text-2xl sm:text-4xl text-ink">Where would you like to start?</h2>
            <p className="text-ink-soft text-sm sm:text-base mt-2 sm:mt-3 max-w-md mx-auto">
              Our bedding is split into two thoughtful collections. Pick the one that fits your home.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {subCollections.map((s) => (
              <Link
                key={s.slug}
                to={`/category/${slug}?sub=${s.slug}`}
                className="group relative aspect-[3/2] sm:aspect-[16/10] overflow-hidden bg-cream-warm"
                data-testid={`sub-collection-${s.slug}`}
              >
                <img src={s.image} alt={s.label} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/45 to-transparent sm:from-ink/75 sm:via-ink/25" />
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 text-cream">
                  <div className="font-display text-2xl sm:text-4xl tracking-tight">{s.label}</div>
                  <div className="text-cream/90 text-xs sm:text-sm mt-1 max-w-sm">{s.description}</div>
                  <div className="mt-3 sm:mt-4 flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold">
                    Browse <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Products section */}
      {(!hasSubCollections || sub || search) && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-16">
          {activeSub && (
            <div className="mb-6 sm:mb-8 flex items-center justify-between flex-wrap gap-2">
              <div>
                <Link to={`/category/${slug}`} className="inline-flex items-center gap-2 min-h-[40px] text-xs uppercase tracking-widest text-ink-soft hover:text-sage" data-testid="sub-back">
                  <ChevronLeft size={12} /> All {cat.label}
                </Link>
                <h2 className="font-display text-2xl sm:text-3xl text-ink">{activeSub.label}</h2>
                <p className="text-ink-soft text-xs sm:text-sm mt-1">{activeSub.description}</p>
              </div>
            </div>
          )}
          {search && (
            <p className="text-ink-soft text-sm mb-6" data-testid="search-results-label">
              Results for "<span className="text-ink font-semibold">{search}</span>"
            </p>
          )}
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-8 sm:gap-x-6 sm:gap-y-12">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-cream-warm" />
                  <div className="h-4 mt-4 bg-cream-warm w-2/3" />
                  <div className="h-3 mt-2 bg-cream-warm w-1/3" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <ComingSoon category={activeSub?.label || cat.label} />
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-8 sm:gap-x-6 sm:gap-y-12">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
